import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../services/api'

// Admin-only auth store since 2026-04-18. The buyer portal (buyerLogin,
// `buyer` state, isBuyerSession branch in checkAuth) was removed along
// with the billing module.
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      permissionsLoaded: false,

      login: async (email, password) => {
        const data = await api.login(email, password)
        set({
          user: data.user,
          isAuthenticated: true,
          permissionsLoaded: false,
        })
        // Fetch permissions after login
        get().fetchPermissions()
        return data
      },

      logout: () => {
        // Fire the server call FIRST so request() reads the current token
        // and builds the Authorization header synchronously (before the
        // first await inside fetch). Then flip local state immediately so
        // the UI reacts without waiting for /auth/logout to respond — a
        // saturated server previously made the button look dead for 5–10s
        // because the state update waited on the awaited request.
        const pending = api.logout()
        pending.catch(err => console.error('Logout server call failed:', err))
        api.setToken(null)
        set({
          user: null,
          isAuthenticated: false,
        })
      },

      checkAuth: async () => {
        // If already authenticated in current session, just mark loading done
        if (get().isAuthenticated && get().user) {
          set({ isLoading: false })
          return
        }

        const token = api.getToken()
        if (!token) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }

        try {
          const data = await api.getMe()
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
          })
          // Fetch permissions for admin users
          get().fetchPermissions()
        } catch (err) {
          console.error('Auth check failed:', err)
          api.setToken(null)
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      isAdmin: () => get().user?.isAdmin === true,

      hasRole: (roleName) => {
        const roles = get().user?.roles || []
        return roles.some((r) => r.name === roleName)
      },

      isFullAdmin: () => {
        const user = get().user
        if (!user?.isAdmin) return false
        const roles = user.roles || []
        // Full admin = has admin role OR has no roles assigned (legacy admin)
        return roles.length === 0 || roles.some((r) => r.name === 'admin')
      },

      /**
       * Fetch user's roles and permissions from the server.
       * Called after login and on auth check.
       */
      fetchPermissions: async () => {
        const user = get().user
        if (!user) {
          set({ permissionsLoaded: true })
          return
        }

        try {
          const response = await api.request('/admin/team/my-permissions')
          if (response.permissions && response.roles) {
            set({
              user: {
                ...get().user,
                permissions: response.permissions,
                roles: response.roles,
              },
              permissionsLoaded: true,
            })
          }
        } catch (err) {
          console.error('[Auth] Failed to fetch permissions:', err)
          // Still mark as loaded to prevent infinite loops
          set({ permissionsLoaded: true })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
